import { Component } from 'react';

/**
 * App-level React error boundary. A single uncaught render/lifecycle error in
 * any child would otherwise unmount the whole tree and leave a blank white page
 * — a very bad first-impression failure mode. This catches it and shows a
 * recoverable fallback instead.
 *
 * Used in two places (see index.js and App.js): once at the ReactDOM root as the
 * ultimate catch-all, and once inside the router around the lazy route tree so a
 * crash in one page shows the fallback without discarding the app shell.
 *
 * Note: error boundaries only catch errors thrown during render, in lifecycle
 * methods, and in constructors of the child tree — NOT in event handlers or
 * async code (those must be handled with try/catch where they occur).
 *
 * Props:
 *   fallback  — optional custom fallback node; defaults to the message below.
 *   children  — the protected subtree.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Keep the detail in the console for developers / support; never surface
    // raw error text to the user.
    console.error('Uncaught error in React tree:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', margin: 0 }}>Something went wrong.</h1>
        <p style={{ maxWidth: 420, color: '#666', margin: 0 }}>
          The page hit an unexpected error. Reloading usually fixes it. If it keeps happening,
          please contact support.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1.25rem',
            cursor: 'pointer',
            borderRadius: 6,
            border: '1px solid #ccc',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
